// Client-side CSV export of whatever table is on screen.

export interface CsvColumn<T> {
  header: string;
  value: (row: T) => unknown;
}

function cell(v: unknown): string {
  if (v === null || v === undefined) return "";
  let s = typeof v === "object" ? JSON.stringify(v) : String(v);
  // Spreadsheet formula injection: a cell starting with = + - @ (or tab/CR) is shown as text.
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function toCsv<T>(rows: T[], columns: CsvColumn<T>[]): string {
  const lines = [columns.map((c) => cell(c.header)).join(",")];
  for (const r of rows) lines.push(columns.map((c) => cell(c.value(r))).join(","));
  return lines.join("\r\n") + "\r\n";
}

export function downloadCsv(filename: string, csv: string): void {
  // A byte-order mark so Excel opens UTF-8 correctly.
  const blob = new Blob(["﻿", csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Reads CSV text (as Excel and Google Sheets save it) into rows of cells: quoted cells, doubled quotes, commas and
 * line breaks inside quotes, CRLF or LF, a leading byte-order mark, and a semicolon or tab separator when the first
 * line has more of those than commas. Blank lines are dropped.
 */
export function parseCsv(text: string): string[][] {
  const src = text.replace(/^﻿/, "");
  const firstLine = src.split(/\r?\n/, 1)[0] ?? "";
  const count = (ch: string) => firstLine.split(ch).length - 1;
  const sep = count(";") > count(",") && count(";") >= count("\t") ? ";" : count("\t") > count(",") ? "\t" : ",";
  const rows: string[][] = [];
  let row: string[] = [];
  let cellText = "";
  let quoted = false;
  for (let i = 0; i < src.length; i++) {
    const c = src[i]!;
    if (quoted) {
      if (c === '"') {
        if (src[i + 1] === '"') {
          cellText += '"';
          i++;
        } else quoted = false;
      } else cellText += c;
    } else if (c === '"' && cellText === "") quoted = true;
    else if (c === sep) {
      row.push(cellText);
      cellText = "";
    } else if (c === "\n" || c === "\r") {
      if (c === "\r" && src[i + 1] === "\n") i++;
      row.push(cellText);
      cellText = "";
      if (row.some((x) => x.trim() !== "")) rows.push(row);
      row = [];
    } else cellText += c;
  }
  row.push(cellText);
  if (row.some((x) => x.trim() !== "")) rows.push(row);
  return rows;
}

export function datedName(base: string): string {
  return `${base}-${new Date().toISOString().slice(0, 10)}.csv`;
}
